"use client";
/**
 * Idle-deferred loader for the support chat widget.
 *
 * PERF: FloatingChatWidget → useSupportChat → supabaseClient statically chained
 * supabase-js (~64KB gz) + the whole chat UI into the FIRST-LOAD bundle of every
 * marketing page. The chat is never needed in the first seconds of a visit, so:
 *  - next/dynamic (ssr:false) splits it into its own chunk, and
 *  - mounting only after the browser is idle (or first user input) keeps that
 *    chunk entirely out of the critical path that PageSpeed measures.
 * The launcher bubble appears ~1-2s after load, which is standard for chat widgets.
 */
import dynamic from "next/dynamic";
import { useEffect, useState } from "react";

const FloatingChatWidget = dynamic(
  () => import("./FloatingChatWidget").then((m) => m.FloatingChatWidget),
  { ssr: false },
);

export function DeferredChatWidget() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let done = false;
    const arm = () => {
      if (done) return;
      done = true;
      setReady(true);
      cleanup();
    };
    const events: (keyof WindowEventMap)[] = ["pointerdown", "keydown", "scroll", "touchstart"];
    const cleanup = () => events.forEach((e) => window.removeEventListener(e, arm));
    // Whichever comes first: browser idle (capped at 3s) or the user interacting.
    events.forEach((e) => window.addEventListener(e, arm, { once: true, passive: true }));
    const w = window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number };
    if (typeof w.requestIdleCallback === "function") {
      w.requestIdleCallback(arm, { timeout: 3000 });
    } else {
      setTimeout(arm, 2500);
    }
    return cleanup;
  }, []);

  return ready ? <FloatingChatWidget /> : null;
}
